import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { menuGroups } from '@/app/menu.generated';
import { routes } from '@/app/routes.generated';
import {
  ApplicationShell,
  Breadcrumb,
  type BreadcrumbNode,
  Navbar,
  ResetDataButton,
  RoleSwitcher,
  SidebarMenu,
  type SidebarMenuGroup,
  type SidebarMenuItem,
} from '@/components/layouts';
import { DialogProvider } from '@/components/layouts/dialogs/DialogProvider';
import { t } from '@/messages/zh-TW';
import { type Role, useSession } from '@/mocks';

// 產生檔的 routeExists=false 代表選單有這個功能、但盤點裡沒有對應頁面
// （目前只有 1 個：instruction/personalizationGPA/showVersion，見 MODULE-FUNCTION-INVENTORY §3.5）。
// ponytail: 直接不渲染會讓選單與原系統對不起來，所以照渲染，只是點了會導到未實作說明頁。
//
// FC-5：依角色過濾（visibleTo 維度）。**依角色而變**，所以不能再是模組層級常數，
// 移到元件內用 useMemo 算。
//
// Q2 裁決（照抄 legacy）：過濾後變空的群組**仍然渲染群組標題**，不整組拿掉——
// legacy 三角色的側邊選單本來就有 4 個「有標題、0 個功能項」的空群組
// （無標題／成績單／學習歷程下載／公告），這不是縮水，是原系統的真實樣子。
export function sidebarGroupsFor(role: Role): readonly SidebarMenuGroup[] {
  return menuGroups.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items
      .filter((item) => item.visibleTo.includes(role))
      .map((item) => ({
        id: item.id,
        label: item.label,
        href: item.routeExists ? item.href : `/_/not-implemented?from=${encodeURIComponent(item.href)}`,
      })),
  }));
}

const ROLE_OPTIONS: readonly { value: Role; label: string }[] = [
  { value: 'admin', label: '系統管理者' },
  { value: 'teacher', label: '教師' },
  { value: 'student', label: t('role.student') },
  { value: 'mentor', label: '導師／臨床教師' },
];

const ROUTE_BY_PATH = new Map(routes.map((route) => [route.path, route]));

function breadcrumbFor(pathname: string): readonly BreadcrumbNode[] {
  const home: BreadcrumbNode = { id: 'home', label: t('nav.home'), href: '/', icon: 'home' };
  const route = ROUTE_BY_PATH.get(pathname);
  if (route === undefined) return [home];
  return [
    home,
    // id 加前綴：/main 這頁的 route.module 剛好也是 'home'，不加前綴會與首頁節點撞 key
    // （React 會噴 "two children with the same key"）。實際踩過。
    { id: `module-${route.module}`, label: route.moduleLabel },
    { id: `page-${route.pageId}`, label: route.title },
  ];
}

/**
 * 全域版型。所有需要側邊選單的頁面都掛在這個 layout route 底下；
 * 登入頁（§8 共用模組 login）刻意不走這裡——原系統登入頁沒有 navbar 與側邊選單。
 */
export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { session, setRole } = useSession();

  const sidebarGroups = useMemo(() => sidebarGroupsFor(session.role), [session.role]);

  const activeItem = sidebarGroups.flatMap((group) => group.items).find(
    (item) => item.href === location.pathname,
  );

  const handleNavigate = (item: SidebarMenuItem): void => {
    navigate(item.href);
  };

  return (
    <DialogProvider>
      <ApplicationShell
        uiId="app-shell"
        collapsed={collapsed}
        navbar={
          <Navbar
            uiId="app-navbar"
            title="ePortfolio 教學管理系統"
            userName={session.displayName}
            onToggleSidebar={() => setCollapsed((previous) => !previous)}
            logoutHref="/index"
            roleSwitcher={
              <RoleSwitcher
                uiId="app-role-switcher"
                options={ROLE_OPTIONS}
                value={session.role}
                onChange={(next) => setRole(next as Role)}
              />
            }
            devToolbar={<ResetDataButton uiId="app-reset-test-data" />}
          />
        }
        sidebar={
          <SidebarMenu
            uiId="app-sidebar"
            groups={sidebarGroups}
            collapsed={collapsed}
            {...(activeItem === undefined ? {} : { activeItemId: activeItem.id })}
            onNavigate={handleNavigate}
          />
        }
        breadcrumb={<Breadcrumb uiId="app-breadcrumb" items={breadcrumbFor(location.pathname)} />}
      >
        <Outlet />
      </ApplicationShell>
    </DialogProvider>
  );
}
